import React, { ReactNode, useMemo, useState } from 'react'
import { differenceInHours } from 'date-fns'
import { removeMarkdown, replaceHtml } from '../../../../../../helpers/string'
import { Linkify } from '../../../../../../components/common/Linkify/Linkify'
import { ChatMessageImages } from '../../../../../../components/chat/ChatMessage/ChatMessageImages/ChatMessageImages'
import { ITicketMessageBoard } from '../../../../../../interfaces/models/ticket.interfaces'
import { useAuth } from '../../../../../../hooks/useAuth'
import { getTimestamp } from '../../../../../../helpers/date'
import { getUserFullName } from '../../../../../../helpers/user'
import { TextTooltip } from '../../../../../../components/common/Tooltip/Tooltip'
import { TicketAvatar } from '../../../../../../components/common/Avatar/Avatar'
import { theme } from '../../../../../../theme/theme'
import { IMediaModel } from '../../../../../../interfaces/models/media.interfaces'
import { ChatMessageDocuments } from '../../../../../../components/chat/ChatMessage/ChatMessageDocuments/ChatMessageDocuments'
import { IDocumentModel } from '../../../../../../interfaces/models/documents.interfaces'

interface ChatMessageInnerProps {
  message: string
  className?: string
  messageAuthorIsCurrentUser: boolean
  media?: IMediaModel[]
  documents?: IDocumentModel[]
}

const ChatMessageInner = ({
  message,
  media,
  documents,
  messageAuthorIsCurrentUser,
  className,
}: ChatMessageInnerProps): React.ReactNode => {
  const processedMessage = useMemo(
    () => removeMarkdown(replaceHtml(message)),
    [message]
  ).trim()

  return (
    <>
      {processedMessage && (
        <div
          className={`inline-block  max-w-xs flex-col whitespace-pre-wrap w-fit
          break-words rounded-2xl py-3 px-[13px] md:max-w-xl
        ${
          messageAuthorIsCurrentUser
            ? 'self-end bg-chat-blue text-white'
            : 'bg-neutral-92'
        }
        ${className ?? ''}
      `}
        >
          <Linkify
            textClassName={`${
              messageAuthorIsCurrentUser ? 'text-white' : 'text-brandGreen'
            }`}
          >
            <span>{processedMessage}</span>
          </Linkify>
        </div>
      )}
      <ChatMessageImages
        media={media}
        messageAuthorIsCurrentUser={messageAuthorIsCurrentUser}
      />
      <ChatMessageDocuments
        documents={documents}
        messageAuthorIsCurrentUser={messageAuthorIsCurrentUser}
      />
    </>
  )
}

interface ChatMessageProps {
  ticketMessage: ITicketMessageBoard
  previousMessage: ITicketMessageBoard | undefined
  nextMessage: ITicketMessageBoard | undefined
  firstMessageInChat?: boolean
}

export const TicketMessage = ({
  ticketMessage,
  previousMessage,
  nextMessage,
  firstMessageInChat,
}: ChatMessageProps): ReactNode => {
  const [referenceElement, setReferenceElement] = useState<Element | null>(null)
  const { userId } = useAuth()
  const { author } = ticketMessage

  const messageAuthorIsCurrentUser = author.id === userId

  const previousMessageSentBySameUser = previousMessage
    ? previousMessage.author.id === author.id
    : false

  const nextMessageSentBySameUser = nextMessage
    ? nextMessage.author.id === author.id
    : false

  const nextMessageSentWithinSameHour = nextMessage
    ? Math.abs(
        differenceInHours(
          Date.parse(nextMessage.createdAt),
          Date.parse(ticketMessage.createdAt)
        )
      ) > 0
    : false

  const timestamp = useMemo(
    () => getTimestamp(new Date(ticketMessage.createdAt)),
    [ticketMessage.createdAt]
  )

  const displayAvatar =
    !nextMessageSentBySameUser && !messageAuthorIsCurrentUser

  const displayName =
    !messageAuthorIsCurrentUser && !previousMessageSentBySameUser

  const showTimestamp = (): boolean => {
    if (nextMessage === undefined) {
      return true
    }
    return nextMessageSentWithinSameHour
  }

  return (
    <div className="flex w-full flex-col">
      {showTimestamp() && (
        <p
          style={theme.textVariants.captionBold}
          className={`flex
            h-[46px] min-w-full items-center justify-center text-neutral-50
            ${firstMessageInChat ? 'mb-5' : 'my-5'}`}
        >
          {timestamp}
        </p>
      )}
      <div
        className={`flex flex-row
          ${
            !nextMessageSentBySameUser && !firstMessageInChat
              ? 'mt-6'
              : 'mt-[3px]'
          }
          ${messageAuthorIsCurrentUser ? 'self-end' : ''}
          `}
      >
        {displayAvatar && (
          <TextTooltip
            title={getUserFullName(author)}
            className="mb-1 mr-2 -ml-8 self-end"
            referenceElement={referenceElement}
            useFlexibleWidth
          >
            <div ref={setReferenceElement}>
              <TicketAvatar user={author} size={24} />
            </div>
          </TextTooltip>
        )}
        <div className="flex flex-col">
          {displayName && (
            <p
              style={theme.textVariants.fineprint}
              className="ml-[10px] text-neutral-30"
            >
              {author?.name || ''}
            </p>
          )}
          <ChatMessageInner
            message={ticketMessage.message || ''}
            media={ticketMessage.media}
            documents={ticketMessage.documents}
            messageAuthorIsCurrentUser={messageAuthorIsCurrentUser}
          />
        </div>
      </div>
    </div>
  )
}
